import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { useMediaQuery } from '@/shared/lib/hooks';
import { Drawer, TimeZoneProvider } from '@/ui';
import { useSession } from '../AppServicesContext';
import { useActiveRecording } from '../session/ActiveRecordingContext';
import { OfflineBanner } from '../shell/OfflineBanner';
import { SectionListeners } from '../shell/SectionListeners';
import { SessionLockGate } from '../shell/SessionLockGate';
import { SideNav } from '../shell/SideNav';
import { TopBar } from '../shell/TopBar';
import s from './Layout.module.css';

/**
 * Основной макет: верхняя панель, боковая навигация по ролям (≥ 1056px — постоянная,
 * уже — в выдвижной панели), содержимое раздела в часовом поясе филиала, блокировка по бездействию.
 */
export function ShellLayout({ area }: { area: 'clinic' | 'backoffice' }) {
  const { t } = useTranslation('shell');
  const { state, membership, roles } = useSession();
  const wide = useMediaQuery('(min-width: 1056px)');
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navRoles = area === 'backoffice' ? (state.user?.globalRoles ?? []) : roles;
  const timeZone = area === 'clinic' ? (membership?.timeZone ?? 'Asia/Almaty') : 'Asia/Almaty';
  // Предупреждения идущей записи (раздел записи) — поверх любого экрана оболочки (TD-21).
  const Overlay = useActiveRecording()?.overlay ?? null;

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (wide) setNavOpen(false);
  }, [wide]);

  return (
    <div className={s.shell}>
      <a className={s.skipLink} href="#main">
        {t('skipToContent')}
      </a>
      <TopBar area={area} onOpenNavigation={() => setNavOpen(true)} navigationOpen={navOpen} />
      <div className={s.body}>
        {wide ? (
          <aside className={s.sideNav}>
            <SideNav area={area} roles={navRoles} />
          </aside>
        ) : (
          <Drawer open={navOpen} onClose={() => setNavOpen(false)} side="left" width={300} title={t('nav.title')} flush>
            <SideNav area={area} roles={navRoles} onNavigate={() => setNavOpen(false)} />
          </Drawer>
        )}
        <main id="main" className={s.main} tabIndex={-1}>
          <OfflineBanner />
          <TimeZoneProvider timeZone={timeZone}>
            <div className={s.content}>
              <Outlet />
            </div>
          </TimeZoneProvider>
        </main>
      </div>
      {Overlay ? <Overlay /> : null}
      <SectionListeners area={area} roles={navRoles} />
      <SessionLockGate />
    </div>
  );
}
